import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { Icon } from '@iconify/react'
import { createPortal } from 'react-dom'
import { useLandingCampaignAchievements } from '@/hooks/useLandingCampaignAchievements'
import { LANDING_PROFILE_ACHIEVEMENTS_ID } from './landing-profile-dock'
import {
  type LandingAchievement,
  type LandingAchievementId,
  achievementScope,
} from '@/lib/landing-achievements'
import {
  ACHIEVEMENT_DEFINITIONS as DEFINITIONS,
  type AchievementDefinition,
} from '@/lib/landing-achievement-definitions'
import styles from './LandingProfileAchievements.module.css'
import { LandingScratchCardModal } from './LandingScratchCardModal'
import { LandingCalendarModal } from './LandingCalendarModal'
import { AchievementCategory } from './AchievementCategory'
import { MonthlyMedals } from './MonthlyMedals'

type LandingProfileAchievementsProps = {
  campaignId: number
  donorId: number
  salesOpen: boolean
  isPurchaseLoading: boolean
  donationOnly: boolean
  isProfileComplete?: boolean
  onPurchase: () => void
  onCompleteProfile?: () => void
  onPersonalizeAvatar?: () => void
  onGoToRoulette?: () => void
  onAvatarBadgeStatus?: (unlocked: boolean, enabled: boolean) => void
  onUnlockedBadgesChange?: (campaignId: number, ids: LandingAchievementId[]) => void
  openAvatarDetailsSignal?: number
  refreshSignal?: number
  onScratchCardChanged?: () => void
  onRewardsChanged?: (changed: boolean) => void
  onViewPrizes?: () => void
  onCelebrationReady?: (ready: boolean) => void
}

export function LandingProfileAchievements({
  campaignId,
  donorId,
  salesOpen,
  isPurchaseLoading,
  donationOnly,
  isProfileComplete = false,
  onPurchase,
  onCompleteProfile = () => {},
  onPersonalizeAvatar = () => {},
  onGoToRoulette,
  onAvatarBadgeStatus,
  onUnlockedBadgesChange,
  openAvatarDetailsSignal = 0,
  refreshSignal = 0,
  onScratchCardChanged,
  onRewardsChanged,
  onViewPrizes = () => {},
  onCelebrationReady,
}: LandingProfileAchievementsProps) {
  const journeyRef = useRef<HTMLElement>(null)
  const { data, isLoading, error } = useLandingCampaignAchievements(
    campaignId,
    refreshSignal,
    donorId
  )
  const storageKey = `legalprime:campaign-achievements:${campaignId}:${donorId}`
  const accountStorageKey = `legalprime:account-achievements:${donorId}`
  const definitions = useMemo(
    () =>
      DEFINITIONS.filter((definition) =>
        data?.achievements.some((item) => item.id === definition.id)
      ).map((definition) => {
        if (definition.id === 'calendar') {
          const target = data?.calendar?.target_days ?? 7
          return {
            ...definition,
            lockedHint: `Acesse seu perfil por ${target} ${target === 1 ? 'dia' : 'dias seguidos'}`,
          }
        }
        if (definition.id !== 'special_supporter') return definition
        const target = Number(
          data?.achievements.find((item) => item.id === 'special_supporter')
            ?.target_value ?? 1000
        )
        const amount = formatCurrency(target > 0 ? target : 1000)
        return {
          ...definition,
          description: `Você acumulou ${amount} em compras nesta campanha.`,
          lockedHint: `Acumule ${amount} em compras`,
        }
      }),
    [data]
  )
  const [seenAchievements, setSeenAchievements] = useState(() => ({
    key: storageKey,
    ids: [
      ...new Set([
        ...readSeenAchievements(storageKey),
        ...readSeenAchievements(accountStorageKey),
      ]),
    ],
  }))
  if (seenAchievements.key !== storageKey) {
    setSeenAchievements({
      key: storageKey,
      ids: [
        ...new Set([
          ...readSeenAchievements(storageKey),
          ...readSeenAchievements(accountStorageKey),
        ]),
      ],
    })
  }
  const seenAchievementIds = seenAchievements.ids
  const notifiedRewards = useRef<typeof data>(null)
  const rewardsRevision = useRef(0)
  useEffect(() => {
    if (data && notifiedRewards.current !== data) {
      notifiedRewards.current = data
      const revision = data.rewards_revision ?? 0
      onRewardsChanged?.(
        data.rewards_changed === true || revision > rewardsRevision.current
      )
      rewardsRevision.current = revision
    }
  }, [data, onRewardsChanged])
  const [selectedBadgeId, setSelectedBadgeId] = useState<LandingAchievementId | null>(
    null
  )
  const [dismissedCalendar, setDismissedCalendar] = useState<string | null>(null)
  const calendar = data?.calendar
  const calendarVisitKey = `${storageKey}:calendar:${calendar?.last_present_on}:${calendar?.completed_at ? 'complete' : 'progress'}`
  const showDailyCalendar = Boolean(
    calendar?.eligible &&
    calendar.checked_in_today &&
    (!calendar.completed_at || calendar.new_check_in) &&
    dismissedCalendar !== calendarVisitKey &&
    !hasSeenCalendar(calendarVisitKey)
  )
  const calendarOpen =
    selectedBadgeId === 'calendar' || (selectedBadgeId === null && showDailyCalendar)
  useEffect(() => {
    if (openAvatarDetailsSignal > 0) {
      queueMicrotask(() => setSelectedBadgeId('avatar_customized'))
    }
  }, [openAvatarDetailsSignal])
  const unlockedIds = useMemo(
    () => data?.achievements.filter((item) => item.unlocked).map((item) => item.id) ?? [],
    [data]
  )
  useEffect(() => {
    if (data)
      onAvatarBadgeStatus?.(
        data.achievements.some(
          (item) => item.id === 'avatar_customized' && item.unlocked
        ),
        data.achievements.some((item) => item.id === 'avatar_customized')
      )
  }, [data, onAvatarBadgeStatus])
  useEffect(() => {
    if (data) onUnlockedBadgesChange?.(campaignId, unlockedIds)
  }, [campaignId, data, onUnlockedBadgesChange, unlockedIds])
  const celebration =
    definitions
      .filter(
        (definition) =>
          definition.id !== 'scratch_card' &&
          !(definition.id === 'calendar' && calendarOpen) &&
          unlockedIds.includes(definition.id) &&
          !seenAchievementIds.includes(definition.id)
      )
      .at(-1) ?? null

  useEffect(() => {
    onCelebrationReady?.(!isLoading && !celebration && !calendarOpen)
  }, [celebration, calendarOpen, isLoading, onCelebrationReady])

  const closeCelebration = useCallback(() => {
    setSeenAchievements({ key: storageKey, ids: unlockedIds })
    writeSeenAchievements(storageKey, unlockedIds)
    writeSeenAchievements(
      accountStorageKey,
      unlockedIds.filter((id) => achievementScope(id) === 'account')
    )
  }, [storageKey, accountStorageKey, unlockedIds])

  const continueJourney = useCallback(() => {
    closeCelebration()
    journeyRef.current?.scrollIntoView?.({
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
      block: 'start',
    })
  }, [closeCelebration])

  useEffect(() => {
    if (!celebration) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeCelebration()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [celebration, closeCelebration])

  const achievementById = new Map(data?.achievements.map((item) => [item.id, item]) ?? [])
  const selectedDefinition = definitions.find(
    (definition) => definition.id === selectedBadgeId
  )
  const selectedAchievement = selectedBadgeId
    ? achievementById.get(selectedBadgeId)
    : undefined
  const calendarAchievement = achievementById.get('calendar')
  const closeCalendar = () => {
    setDismissedCalendar(calendarVisitKey)
    try {
      localStorage.setItem(calendarVisitKey, 'seen')
    } catch {
      /* Display-only preference. */
    }
    setSelectedBadgeId(null)
    if (calendarAchievement?.unlocked) {
      const seen = [...new Set([...seenAchievementIds, 'calendar' as const])]
      setSeenAchievements({ key: storageKey, ids: seen })
      writeSeenAchievements(storageKey, seen)
    }
  }
  useEffect(() => {
    if (
      !selectedBadgeId ||
      selectedBadgeId === 'scratch_card' ||
      selectedBadgeId === 'calendar'
    )
      return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedBadgeId(null)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [selectedBadgeId])
  const nextDefinition = definitions.find(
    (definition) =>
      !achievementById.get(definition.id)?.unlocked &&
      (definition.id !== 'scratch_card' || data?.scratch_card?.available)
  )
  const nextAchievement = nextDefinition
    ? achievementById.get(nextDefinition.id)
    : undefined
  const unlockedCount = unlockedIds.length

  return (
    <section
      id={LANDING_PROFILE_ACHIEVEMENTS_ID}
      ref={journeyRef}
      className={styles.journey}
      aria-labelledby="campaign-achievements-title"
    >
      <header className={styles.header}>
        <p className={styles.kicker}>Sua jornada de impacto</p>
        <div
          className={styles.score}
          aria-label={`${unlockedCount} de ${definitions.length} conquistas desbloqueadas`}
        >
          <Icon icon="mdi:star-four-points" aria-hidden />
          <strong>{unlockedCount}</strong>
          <span>/ {definitions.length}</span>
        </div>
        <h2 id="campaign-achievements-title" className={styles.title}>
          Colecione conquistas
        </h2>
        <p className={styles.lead}>
          Seu perfil e avatar acompanham sua conta. Compras e recompensas pertencem à
          campanha atual.
        </p>
      </header>

      {isLoading ? (
        <div
          className={styles.loading}
          aria-label="Carregando conquistas"
          aria-busy="true"
        >
          <span />
          <span />
          <span />
        </div>
      ) : error ? (
        <p className={styles.error} role="status">
          {error}
        </p>
      ) : (
        <>
          {(['account', 'campaign'] as const).map((scope) => (
            <AchievementCategory
              key={scope}
              title={scope === 'account' ? 'Selos da conta' : 'Selos da campanha'}
              totalCount={
                definitions.filter(
                  (definition) => achievementScope(definition.id) === scope
                ).length
              }
              earnedCount={
                definitions.filter(
                  (definition) =>
                    achievementScope(definition.id) === scope &&
                    achievementById.get(definition.id)?.unlocked
                ).length
              }
              description={
                scope === 'account'
                  ? 'Suas conquistas acompanham você em todas as campanhas.'
                  : 'Conquistas exclusivas da campanha em que você está participando.'
              }
            >
              {definitions
                .filter((definition) => achievementScope(definition.id) === scope)
                .map((definition) => {
                  const achievement = achievementById.get(definition.id)
                  const unlocked = achievement?.unlocked === true
                  return (
                    <button
                      type="button"
                      key={definition.id}
                      className={unlocked ? styles.badgeUnlocked : styles.badgeLocked}
                      aria-label={`${definition.title}: ${unlocked ? 'desbloqueada' : 'bloqueada'}`}
                      title={definition.title}
                      onClick={() => {
                        if (definition.id === 'profile_complete' && !unlocked) {
                          onCompleteProfile()
                          return
                        }
                        setSelectedBadgeId(definition.id)
                      }}
                    >
                      <div className={styles.badgeVisual}>
                        <img
                          src={definition.image}
                          alt=""
                          className={styles.badgeImage}
                        />
                        {!unlocked ? (
                          <span className={styles.lock} aria-hidden>
                            <Icon icon="mdi:lock" />
                          </span>
                        ) : (
                          <Icon
                            icon="mdi:check-decagram"
                            className={styles.check}
                            aria-hidden
                          />
                        )}
                      </div>
                      <p className={styles.badgeName}>{definition.title}</p>
                      <p className={styles.accessibleBadgeText}>
                        {unlocked
                          ? 'Conquistada!'
                          : definition.id === 'avatar_customized' && !isProfileComplete
                            ? 'Complete seu perfil primeiro'
                            : definition.id === 'scratch_card'
                              ? data?.scratch_card?.attempt
                                ? data.scratch_card.attempt.revealed_at
                                  ? 'Veja seu resultado'
                                  : 'Continue sua raspadinha'
                                : data?.scratch_card?.available
                                  ? 'Grátis · toque para raspar'
                                  : 'Em breve'
                              : definition.lockedHint}
                      </p>
                    </button>
                  )
                })}
              {!definitions.some(
                (definition) => achievementScope(definition.id) === scope
              ) && (
                <p className={styles.categoryEmpty}>
                  Nenhum selo disponível nesta categoria.
                </p>
              )}
            </AchievementCategory>
          ))}
          <MonthlyMedals
            achievements={data?.monthly_medals}
            campaignId={campaignId}
            donorId={donorId}
            onViewPrizes={onViewPrizes}
          />

          {definitions.length > 0 && (
            <div className={styles.mission}>
              {nextDefinition && nextAchievement ? (
                <>
                  <div className={styles.missionCopy}>
                    <span className={styles.missionIcon} aria-hidden>
                      <Icon icon="mdi:flag-checkered" />
                    </span>
                    <div>
                      <p className={styles.missionLabel}>Próxima missão</p>
                      <p className={styles.missionTitle}>{nextDefinition.lockedHint}</p>
                    </div>
                    <strong className={styles.progressValue}>
                      {formatProgress(nextAchievement)}
                    </strong>
                  </div>
                  <div
                    className={styles.progressTrack}
                    role="progressbar"
                    aria-label={`Progresso para ${nextDefinition.title}`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progressPercent(nextAchievement)}
                  >
                    <span
                      style={
                        {
                          '--achievement-progress': `${progressPercent(nextAchievement)}%`,
                        } as CSSProperties
                      }
                    />
                  </div>
                  {nextDefinition.id === 'calendar' ? (
                    <button
                      type="button"
                      className={styles.cta}
                      onClick={() => setSelectedBadgeId('calendar')}
                    >
                      <Icon icon="mdi:calendar-check" aria-hidden /> Ver meu calendário
                    </button>
                  ) : nextDefinition.id === 'roulette' ? (
                    <button
                      type="button"
                      className={styles.cta}
                      onClick={onGoToRoulette ?? (() => setSelectedBadgeId('roulette'))}
                    >
                      <Icon icon="mdi:ferris-wheel" aria-hidden /> Ver roleta
                    </button>
                  ) : nextDefinition.id === 'scratch_card' ? (
                    <button
                      type="button"
                      className={styles.cta}
                      onClick={() => setSelectedBadgeId('scratch_card')}
                    >
                      <Icon icon="mdi:gesture-swipe" aria-hidden /> Fazer minha raspadinha
                    </button>
                  ) : nextDefinition.id === 'profile_complete' ? (
                    <button
                      type="button"
                      className={styles.cta}
                      onClick={onCompleteProfile}
                    >
                      <Icon icon="mdi:account-edit-outline" aria-hidden />
                      Completar meu perfil
                    </button>
                  ) : nextDefinition.id === 'avatar_customized' ? (
                    <button
                      type="button"
                      className={styles.cta}
                      onClick={onPersonalizeAvatar}
                    >
                      <Icon icon="mdi:palette-outline" aria-hidden /> Personalizar meu
                      avatar
                    </button>
                  ) : salesOpen ? (
                    <button
                      type="button"
                      className={styles.cta}
                      onClick={onPurchase}
                      disabled={isPurchaseLoading}
                    >
                      <Icon
                        icon={isPurchaseLoading ? 'mdi:loading' : 'mdi:rocket-launch'}
                        aria-hidden
                      />
                      {isPurchaseLoading
                        ? 'Abrindo...'
                        : donationOnly
                          ? 'Avançar com nova compra'
                          : 'Avançar com nova compra'}
                    </button>
                  ) : null}
                </>
              ) : (
                <div className={styles.complete}>
                  <Icon icon="mdi:crown" aria-hidden />
                  <div>
                    <p className={styles.missionLabel}>
                      {unlockedCount === definitions.length
                        ? 'Coleção completa'
                        : 'Sua jornada'}
                    </p>
                    <p className={styles.missionTitle}>
                      {unlockedCount === definitions.length
                        ? `Você conquistou os ${definitions.length} selos disponíveis!`
                        : `Você já conquistou ${unlockedCount} selos. Obrigado por fazer parte!`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {celebration
        ? createPortal(
            <AchievementCelebration
              achievement={celebration}
              onClose={closeCelebration}
              onContinue={continueJourney}
            />,
            document.body
          )
        : null}
      {selectedBadgeId === 'scratch_card' && !celebration && (
        <LandingScratchCardModal
          campaignId={campaignId}
          state={data?.scratch_card}
          rewards={selectedAchievement?.rewards}
          onClose={(changed) => {
            setSelectedBadgeId(null)
            if (changed) onScratchCardChanged?.()
          }}
          onViewPrizes={onViewPrizes}
        />
      )}
      {selectedDefinition &&
      selectedDefinition.id !== 'scratch_card' &&
      selectedDefinition.id !== 'calendar' &&
      selectedAchievement &&
      !celebration
        ? createPortal(
            <AchievementDetails
              definition={selectedDefinition}
              achievement={selectedAchievement}
              salesOpen={salesOpen}
              donationOnly={donationOnly}
              isPurchaseLoading={isPurchaseLoading}
              onPurchase={onPurchase}
              onCompleteProfile={onCompleteProfile}
              onPersonalizeAvatar={onPersonalizeAvatar}
              onGoToRoulette={onGoToRoulette}
              onClose={() => setSelectedBadgeId(null)}
            />,
            document.body
          )
        : null}
      {calendarOpen && calendar && calendarAchievement && !celebration && (
        <LandingCalendarModal
          state={calendar}
          achievement={calendarAchievement}
          onClose={closeCalendar}
          onCompleteProfile={onCompleteProfile}
          onViewPrizes={onViewPrizes}
        />
      )}
    </section>
  )
}

function AchievementDetails({
  definition,
  achievement,
  salesOpen,
  donationOnly,
  isPurchaseLoading,
  onPurchase,
  onCompleteProfile,
  onPersonalizeAvatar,
  onGoToRoulette,
  onClose,
}: {
  definition: AchievementDefinition
  achievement: LandingAchievement
  salesOpen: boolean
  donationOnly: boolean
  isPurchaseLoading: boolean
  onPurchase: () => void
  onCompleteProfile: () => void
  onPersonalizeAvatar: () => void
  onGoToRoulette?: () => void
  onClose: () => void
}) {
  const unlocked = achievement.unlocked
  return (
    <div
      className={styles.celebrationOverlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className={`${styles.celebration} ${styles.detailModal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="achievement-detail-title"
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Fechar detalhes do selo"
          autoFocus
        >
          <Icon icon="mdi:close" />
        </button>
        <p className={styles.celebrationKicker}>
          {unlocked ? 'Selo conquistado!' : 'Próximo desafio'}
        </p>
        <img
          src={definition.image}
          alt=""
          className={`${styles.detailImage} ${unlocked ? '' : styles.detailImageLocked}`}
        />
        <p className={styles.celebrationEyebrow}>{definition.eyebrow}</p>
        <h2 id="achievement-detail-title">
          {unlocked ? (achievement.content?.title ?? definition.title) : definition.title}
        </h2>
        {achievement.rewards &&
        (achievement.rewards.coupon_percent ||
          achievement.rewards.lucky_numbers ||
          achievement.rewards.roulette_spins) ? (
          <div className={styles.detailText}>
            <strong>
              {unlocked
                ? 'Seus extras desta conquista'
                : 'Ao conquistar, você também ganha'}
            </strong>
            <ul>
              {achievement.rewards.coupon_percent ? (
                <li>
                  Cupom de {achievement.rewards.coupon_percent}% para a próxima compra.
                </li>
              ) : null}
              {achievement.rewards.lucky_numbers > 0 ? (
                <li>
                  {achievement.rewards.lucky_numbers} números extras para resgatar na aba
                  Números.
                </li>
              ) : null}
              {achievement.rewards.roulette_spins > 0 ? (
                <li>
                  {achievement.rewards.roulette_spins} giros extras. Compartilhe a
                  campanha para liberar a roleta.
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
        {unlocked ? (
          <>
            {achievement.content?.text ? (
              <p className={styles.detailText}>{achievement.content.text}</p>
            ) : (
              <p className={styles.detailText}>{definition.description}</p>
            )}
            {achievement.content?.video_url ? (
              <div className={styles.videoFrame}>
                <iframe
                  src={`${achievement.content.video_url}?controls=0&fs=0&rel=0&playsinline=1&iv_load_policy=3`}
                  title={`Vídeo de agradecimento: ${definition.title}`}
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  // Restrict popups, top-level navigation and downloads. This does
                  // not conceal the video URL or override YouTube's own interface.
                  sandbox="allow-scripts allow-same-origin"
                  allow="encrypted-media; fullscreen 'none'; picture-in-picture 'none'; web-share 'none'; clipboard-write 'none'; presentation 'none'"
                />
              </div>
            ) : null}
          </>
        ) : (
          <>
            <p className={styles.detailText}>
              {achievement.locked_reason?.trim() ||
                (definition.id === 'profile_complete'
                  ? 'Preencha todos os dados solicitados na aba Cadastro para chegar a 100% e conquistar este selo.'
                  : definition.id === 'avatar_customized'
                    ? 'Abra o editor do seu avatar, escolha uma aparência e salve para conquistar este selo nesta campanha.'
                    : definition.id === 'roulette'
                      ? 'Gire a roleta desta campanha pelo menos uma vez. Qualquer resultado vale para conquistar o selo, mesmo um giro sem prêmio.'
                      : `Para desbloquear este selo, ${definition.lockedHint.toLowerCase()} nesta campanha. Seu progresso é contabilizado após a confirmação do pagamento.`)}
            </p>
            <p className={styles.detailProgress}>
              Seu progresso: {formatProgress(achievement)}
            </p>
            {definition.id === 'roulette' ? (
              onGoToRoulette ? (
                <button
                  type="button"
                  className={styles.celebrationButton}
                  onClick={() => {
                    onClose()
                    onGoToRoulette()
                  }}
                >
                  Ir para a roleta
                </button>
              ) : null
            ) : definition.id === 'avatar_customized' ? (
              <button
                type="button"
                className={styles.celebrationButton}
                onClick={() => {
                  onClose()
                  onPersonalizeAvatar()
                }}
              >
                Personalizar meu avatar
              </button>
            ) : definition.id === 'profile_complete' ? (
              <button
                type="button"
                className={styles.celebrationButton}
                onClick={() => {
                  onClose()
                  onCompleteProfile()
                }}
              >
                Completar meu perfil
              </button>
            ) : salesOpen ? (
              <button
                type="button"
                className={styles.celebrationButton}
                disabled={isPurchaseLoading}
                onClick={() => {
                  onClose()
                  onPurchase()
                }}
              >
                {isPurchaseLoading
                  ? 'Abrindo...'
                  : donationOnly
                    ? 'Fazer nova compra'
                    : 'Fazer nova compra'}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

function AchievementCelebration({
  achievement,
  onClose,
  onContinue,
}: {
  achievement: AchievementDefinition
  onClose: () => void
  onContinue: () => void
}) {
  return (
    <div
      className={styles.celebrationOverlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className={styles.confetti} aria-hidden>
        {Array.from({ length: 18 }, (_, index) => (
          <i key={index} />
        ))}
      </div>
      <div
        className={styles.celebration}
        role="dialog"
        aria-modal="true"
        aria-labelledby="achievement-unlocked-title"
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Fechar conquista"
        >
          <Icon icon="mdi:close" />
        </button>
        <p className={styles.celebrationKicker}>Nova conquista desbloqueada!</p>
        <div className={styles.burst} aria-hidden />
        <img src={achievement.image} alt="" className={styles.celebrationImage} />
        <p className={styles.celebrationEyebrow}>{achievement.eyebrow}</p>
        <h2 id="achievement-unlocked-title">{achievement.title}</h2>
        <p className={styles.celebrationText}>{achievement.description}</p>
        <button
          type="button"
          className={styles.celebrationButton}
          onClick={onContinue}
          autoFocus
        >
          Continuar minha jornada
        </button>
      </div>
    </div>
  )
}

function progressPercent(achievement: LandingAchievement): number {
  const current = Number(achievement.current_value)
  const target = Number(achievement.target_value)
  if (!Number.isFinite(current) || !Number.isFinite(target) || target <= 0) return 0
  const percent = Math.min(100, Math.max(0, Math.round((current / target) * 100)))
  return achievement.unlocked ? percent : Math.min(99, percent)
}

function formatProgress(achievement: LandingAchievement): string {
  if (achievement.id === 'profile_complete') {
    return `${Math.round(Number(achievement.current_value))}% / 100%`
  }
  if (achievement.id === 'special_supporter') {
    return `${formatCurrency(Number(achievement.current_value))} / ${formatCurrency(Number(achievement.target_value))}`
  }
  return `${Math.floor(Number(achievement.current_value))} / ${Math.floor(Number(achievement.target_value))}`
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0)
}

function readSeenAchievements(key: string): LandingAchievementId[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? '[]') as unknown
    return Array.isArray(parsed)
      ? parsed.filter((id): id is LandingAchievementId =>
          DEFINITIONS.some((definition) => definition.id === id)
        )
      : []
  } catch {
    return []
  }
}

function hasSeenCalendar(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'seen'
  } catch {
    return false
  }
}

function writeSeenAchievements(key: string, ids: LandingAchievementId[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    // A coleção continua funcional quando o navegador bloqueia armazenamento local.
  }
}
